import * as React from 'react';
import PropTypes from 'prop-types';
import Button from '@base/Button/Button';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import Paper from '@base/Paper/Paper';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import Drawer from '@base/Drawer/Drawer';
import H5 from '@base/H5/H5';
import Image from '@base/Image';

const Container = styled(Paper)`
  &.container {
    @media (max-width: 47.95em) {
      padding: 24px 16px;
      min-height: 180px;
      margin: 0 16px;
      top: -32px;
      width: calc(100% - 32px);
      position: relative;
      overflow: hidden;
    }
  }
`;

const ImageWrapper = styled.div`
  width: 124px;
  height: 124px;
  position: absolute;
  right: -27px;
  top: -26px;
`;

const Heading = styled(H2)`
  font-size: 22px;
  &.mb24 {
    margin-bottom: 24px;
  }
`;

const SubHeading = styled(P1)`
  margin: 8px 0 16px;
  width: calc(100% - 80px);
`;

const LearnMore = styled(Button)`
  background-color: var(--color-i-white);
  min-height: 40px;
  height: 40px;
  color: var(--color-i-green);
  border: 1px solid var(--color-divider);
  display: inline-block;
  width: 114px;
  border-radius: 8px;
  padding: 0;
  font-size: 14px;
`;

const ButtonContainer = styled.div`
  position: fixed;
  height: 72px;
  bottom: 0;
  left: 0;
  width: 100%;
  background: var(--color-i-white);
  box-shadow: inset 0px 1px 0px var(--color-shadow);
  padding: 12px 16px;
`;

const ViewAllPricing = styled(Button)`
  width: 100%;
  height: 100%;
  min-height: 100%;
`;

const DrawerContainer = styled.div`
  grid-column: span 10;
`;

const Card = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  &.last {
    margin-bottom: 96px;
  }
`;
const Icon = styled.div`
  margin-right: 16px;
`;

const CardTitle = styled(H5)`
  margin-bottom: 8px;
`;

const Logo = styled.div`
  width: 116px;
  height: 72px;
  margin-bottom: 12px;
`;
function MobileIconicSection({ type, info, className }) {
  const { heading, subHeading, data, logo } = info;
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const { push, route, asPath } = useRouter();
  const onLearnMoreClick = () => {
    setOpenDrawer(true);
  };
  const closeDrawer = () => {
    setOpenDrawer(false);
  };
  const onViewAllPricing = () => {
    push(`${route}/subscribe`, `${asPath}/subscribe`);
  };
  return (
    <>
      <Container className={`container ${className || ''}`}>
        <Heading>{heading}</Heading>
        <SubHeading>{subHeading}</SubHeading>
        <LearnMore label="Learn More" onClick={onLearnMoreClick} />
        <ImageWrapper>
          <Image
            src={`goal/${type.toLowerCase()}_1.png`}
            width={124}
            height={124}
            alt={type}
          />
        </ImageWrapper>
      </Container>
      <Drawer drawerState={openDrawer} onClose={closeDrawer}>
        <DrawerContainer>
          {logo && (
            <Logo>
              <Image src={logo} width={116} height={72} alt={type} />
            </Logo>
          )}
          <Heading className="mb24">{`${type} Subscription`}</Heading>
          {data?.map((card, index) => {
            const {
              name,
              description,
              image,
              appIcon,
              benefitDesc,
              benefitHeader
            } = card;
            const icon = appIcon ?? (image || '');
            return (
              <Card
                key={String(index)}
                className={data.length - 1 === index ? 'last' : ''}
              >
                {icon ? (
                  <Icon>
                    <Image
                      src={icon}
                      alt={name || benefitHeader}
                      width={48}
                      height={48}
                    />
                  </Icon>
                ) : null}
                <div>
                  <CardTitle>{name || benefitHeader}</CardTitle>
                  <P1>{description || benefitDesc}</P1>
                </div>
              </Card>
            );
          })}
        </DrawerContainer>
        <ButtonContainer>
          <ViewAllPricing label="View all plans" onClick={onViewAllPricing} />
        </ButtonContainer>
      </Drawer>
    </>
  );
}

const MemoizedMobileIconicSection = React.memo(MobileIconicSection);

export default MemoizedMobileIconicSection;
MobileIconicSection.propTypes = {
  type: PropTypes.string.isRequired,
  info: PropTypes.objectOf(PropTypes.any).isRequired,
  className: PropTypes.string
};

MobileIconicSection.defaultProps = {
  className: ''
};
